import { DateTimeCell } from '../../order/order-table-cells';
import { displayMoneyFormat } from '../../../util/util';
import { Button } from '../../../components/form/button';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import React, { ReactNode, useContext, useState } from 'react';
import { PaymentMethodFormat, PaymentStatusBadge } from '../invoice-list';
import { Payment } from '../../../clients/quipli-service/payment/payment';
import { TenantContext } from '../../../context/tenant-context';
import { toast } from '../../../util/toasts';
import { useInvoiceApiV1 } from '../../../clients/quipli-service/invoice/invoice-api-v1';
import { Invoice } from '../../../clients/quipli-service/invoice/invoice';
import { Typography } from '../../../components/typography/typography';
import { RefundStatementPaymentModal } from '../../payment/refund-statement-payment-modal';
import { useAdmin } from '../../../hooks/use-admin-context';
import { Constants } from '../../../constants/constants';

export const PaymentHistory = ({
  payments,
  refresh,
  title = 'Payment History',
  invoice,
}: {
  payments: Payment[];
  refresh?: (sortBy?: 'date' | 'dueDate' | 'invoiceNumber') => Promise<any>;
  title?: string;
  invoice?: Invoice;
}) => {
  const { deletePayment } = useInvoiceApiV1();
  const { hasPermissions } = useAdmin();
  const tenant = useContext(TenantContext);
  const [confirmModal, setConfirmModal] = useState<ReactNode | undefined>();
  const [refundModal, setRefundModal] = useState<ReactNode | undefined>();

  async function onDeletePayment(payment: Payment, invoiceId: string) {
    try {
      await deletePayment(invoiceId, payment.id);
      toast('Payment deleted successfully');
    } catch (e: any) {
      toast('Failed to delete payment', 'error');
    } finally {
      setConfirmModal(undefined);
      refresh && (await refresh());
    }
  }

  return (
    <div>
      <h6>{title}</h6>

      <table className="w-full">
        <thead>
          <tr>
            <th className="text-left">Method</th>
            <th className="text-left">Date</th>
            <th className="text-left">Txn Id</th>
            <th className="text-left">Status</th>
            <th className="text-right">Amount</th>
            <th className="text-right"></th>
          </tr>
        </thead>
        <tbody>
          {!invoice ? (
            payments
              .filter((_) => _.methodDetail != 'Pending Capture')
              .map((p) => {
                return (
                  <tr key={p.id}>
                    <td className="text-left">{PaymentMethodFormat(p.method, p.methodDetail)}</td>
                    <td className="text-left">
                      <DateTimeCell date={p.paidAt} />
                    </td>
                    <td className="text-left">{p.gatewayId}</td>
                    <td className="text-left">{PaymentStatusBadge(p)}</td>
                    <td className="text-right">
                      {displayMoneyFormat(
                        p.availableAmount ? p.availableAmount * (p.method == 'Credit' ? -1 : 1) : undefined,
                        tenant.configuration!.currency,
                      )}
                    </td>
                    <td className="text-right flex justify-end">
                      {p.method !== 'Credit' && (
                        <Button
                          className="button button-primary m-1"
                          disabled={!hasPermissions(Constants.Permissions.Refund_Upcharge_Order)}
                          onClick={() =>
                            setRefundModal(
                              <RefundStatementPaymentModal
                                payment={p}
                                isOpen={true}
                                close={async () => {
                                  refresh && (await refresh());
                                  setRefundModal(undefined);
                                }}
                                currency={tenant.configuration!.currency}
                              />,
                            )
                          }
                        >
                          <Typography type={'text-sm'}>Issue Refund</Typography>
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
          ) : payments.length > 0 ? (
            payments
              .filter((_) => _.methodDetail != 'Pending Capture')
              .flatMap((p) =>
                p.invoicePayments
                  .filter((ip) => ip.invoiceId == invoice.id)
                  .map((ip) => {
                    return { payment: p, invoicePayment: ip };
                  }),
              )
              .map(({ payment: p, invoicePayment }) => {
                const invoicePaymentAmount = invoicePayment.amount;
                return (
                  <tr key={p.id}>
                    <td className="text-left">{PaymentMethodFormat(p.method, p.methodDetail)}</td>
                    <td className="text-left">
                      <DateTimeCell date={p.paidAt} />
                    </td>
                    <td className="text-left">{p.gatewayId}</td>
                    <td className="text-left">{PaymentStatusBadge(p)}</td>
                    <td className="text-right">
                      {displayMoneyFormat(invoicePaymentAmount, tenant.configuration!.currency)}
                    </td>
                    <td>
                      <div className="flex justify-center items-center">
                        {p.method !== 'Stripe' && (
                          <Button
                            className="button icon-button"
                            disabled={!hasPermissions(Constants.Permissions.Refund_Upcharge_Order)}
                            onClick={() =>
                              setConfirmModal(
                                <ConfirmModal
                                  isOpen={true}
                                  close={() => setConfirmModal(false)}
                                  onConfirm={() => onDeletePayment(p, invoice.id)}
                                  prompt={`Are you sure you want to delete ${PaymentMethodFormat(
                                    p.method,
                                    p.methodDetail,
                                  )} payment for ${displayMoneyFormat(
                                    invoicePaymentAmount,
                                    tenant.configuration!.currency,
                                  )}?`}
                                />,
                              )
                            }
                          >
                            <i className="fa fa-trash-can" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
          ) : (
            <tr>
              <td className="text-center text-[var(--grey-500)]" colSpan={100}>
                None
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {confirmModal}
      {refundModal}
    </div>
  );
};
